// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — two round buttons under auto-rotate, in the top inline-end corner
 * (owner, 2026-10-19):
 *
 *   [ ? Help ]
 *   [ ⟳ ]    spin (autoRotateControl.ts)
 *   [ ⌖ ]    Reset: the globe back to its default view
 *   [ 2D ]   the flat 2:1 map; on it, the button reads 3D and brings the
 *            globe back
 *
 * Reset is the double-click reset, as a button. Both act on the primary
 * globe; the camera sync carries a reset to the others. While a Khipu plays
 * or this device follows a room's lead, both wait (`suspended`): the camera
 * is someone else's then.
 */

import { t } from '../i18n'

/** What the control drives: the primary globe. */
export interface GlobeViewTarget {
  resetView(): void
}

/** The flat map, once a host offers it. Without one the 2D button stays hidden. */
export interface FlatViewSwitch {
  isFlat(): boolean
  setFlat(on: boolean): void
}

export interface GlobeViewDeps {
  primary(): GlobeViewTarget | null
  flat?: FlatViewSwitch
  announce?(message: string): void
  /** True while something else drives the camera (a playing Khipu, a room's lead). */
  suspended?(): boolean
  /** Analytics hook: which button, and for 2D/3D the view chosen. */
  onChange?(what: 'reset' | 'flat' | 'globe'): void
}

export interface GlobeViewHandle {
  /** Show the 2D/3D state and whether the buttons can act now. */
  sync(): void
}

let syncTimer: number | null = null

/** Wire `#globe-view-control`. Safe to call twice. */
export function initGlobeViewControl(deps: GlobeViewDeps): GlobeViewHandle {
  const root = document.getElementById('globe-view-control')
  const reset = document.getElementById('globe-reset-btn') as HTMLButtonElement | null
  const flat = document.getElementById('globe-flat-btn') as HTMLButtonElement | null
  if (!root || !reset || !flat) return { sync: () => {} }

  const sync = (): void => {
    const waiting = deps.suspended?.() ?? false
    reset.disabled = waiting
    flat.hidden = !deps.flat
    if (!deps.flat) return
    flat.disabled = waiting
    const isFlat = deps.flat.isFlat()
    // The button names what pressing it shows.
    const label = t(isFlat ? 'globeView.toGlobe' : 'globeView.toFlat')
    flat.title = label
    flat.setAttribute('aria-label', label)
    flat.setAttribute('aria-pressed', String(isFlat))
    flat.classList.toggle('active', isFlat)
    const text = flat.querySelector('.globe-flat-text')
    if (text) text.textContent = isFlat ? '3D' : '2D'
  }

  if (root.dataset.wired !== 'true') {
    root.dataset.wired = 'true'
    reset.addEventListener('click', () => {
      if (deps.suspended?.()) return
      const primary = deps.primary()
      if (!primary) return
      primary.resetView()
      deps.onChange?.('reset')
      deps.announce?.(t('globeView.announce.reset'))
    })
    flat.addEventListener('click', () => {
      if (!deps.flat || deps.suspended?.()) return
      const next = !deps.flat.isFlat()
      deps.flat.setFlat(next)
      sync()
      deps.onChange?.(next ? 'flat' : 'globe')
      deps.announce?.(t(next ? 'globeView.announce.flat' : 'globeView.announce.globe'))
    })
  }

  // A Khipu starting or a room taking the lead says nothing here: look, as auto-rotate does.
  if (syncTimer !== null) window.clearInterval(syncTimer)
  syncTimer = window.setInterval(sync, 500)
  sync()
  return { sync }
}
